import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './AdminServiceEditor.css';

const AdminServiceEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        description: '',
        icon: 'flaticon-quran',
        image_url: '',
        page_intro: '',
        section_title: '',
        section_content: '',
        highlight_title: '',
        highlight_content: '',
        features: [],
        quote: '',
        sort_order: 0
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [newFeature, setNewFeature] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('pcic_admin_token');
        if (!token) {
            navigate('/admin');
            return;
        }

        if (!isNew) {
            fetchService();
        }
    }, [id, isNew, navigate]);

    const fetchService = async () => {
        try {
            const res = await fetch(`/api/services/${id}`);
            const data = await res.json();
            if (data.features) {
                data.features = JSON.parse(data.features);
            }
            setFormData(data);
        } catch (error) {
            alert('Error loading service');
            navigate('/admin/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addFeature = () => {
        if (newFeature.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, newFeature.trim()]
            }));
            setNewFeature('');
        }
    };

    const removeFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const url = isNew ? '/api/services' : `/api/services/${id}`;
            const method = isNew ? 'POST' : 'PUT';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                navigate('/admin/dashboard');
            } else {
                const error = await res.json();
                alert(error.message || 'Error saving service');
            }
        } catch (error) {
            alert('Error saving service');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-editor">
            <header className="editor-header">
                <Link to="/admin/dashboard" className="back-link">← Back to Dashboard</Link>
                <h1>{isNew ? 'Add New Service' : 'Edit Service'}</h1>
            </header>

            <form onSubmit={handleSubmit} className="editor-form">
                <div className="form-group">
                    <label>Title *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Subtitle</label>
                    <input
                        type="text"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Icon Class</label>
                        <input
                            type="text"
                            name="icon"
                            value={formData.icon}
                            onChange={handleChange}
                            placeholder="flaticon-quran"
                        />
                    </div>

                    <div className="form-group">
                        <label>Sort Order</label>
                        <input
                            type="number"
                            name="sort_order"
                            value={formData.sort_order}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Image URL</label>
                    <input
                        type="text"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Page Introduction</label>
                    <textarea
                        name="page_intro"
                        value={formData.page_intro}
                        onChange={handleChange}
                        rows={3}
                    />
                </div>

                <div className="form-group">
                    <label>Section Title</label>
                    <input
                        type="text"
                        name="section_title"
                        value={formData.section_title}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Section Content</label>
                    <textarea
                        name="section_content"
                        value={formData.section_content}
                        onChange={handleChange}
                        rows={4}
                    />
                </div>

                <div className="form-group">
                    <label>Highlight Title</label>
                    <input
                        type="text"
                        name="highlight_title"
                        value={formData.highlight_title}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Highlight Content</label>
                    <textarea
                        name="highlight_content"
                        value={formData.highlight_content}
                        onChange={handleChange}
                        rows={3}
                    />
                </div>

                <div className="form-group">
                    <label>Features</label>
                    <div className="features-list">
                        {formData.features.map((feature, index) => (
                            <div key={index} className="feature-item">
                                <span>{feature}</span>
                                <button type="button" onClick={() => removeFeature(index)}>×</button>
                            </div>
                        ))}
                    </div>
                    <div className="feature-add">
                        <input
                            type="text"
                            value={newFeature}
                            onChange={(e) => setNewFeature(e.target.value)}
                            placeholder="Add a feature"
                        />
                        <button type="button" onClick={addFeature}>Add</button>
                    </div>
                </div>

                <div className="form-group">
                    <label>Quote</label>
                    <textarea
                        name="quote"
                        value={formData.quote}
                        onChange={handleChange}
                        rows={2}
                    />
                </div>

                <div className="form-actions">
                    <button type="submit" disabled={saving} className="save-btn">
                        {saving ? 'Saving...' : 'Save Service'}
                    </button>
                    <Link to="/admin/dashboard" className="cancel-btn">Cancel</Link>
                </div>
            </form>
        </div>
    );
};

export default AdminServiceEditor;
